/**
 * WMS v2 types — mirror the nest-logistic-service warehouse entities
 * (`src/modules/warehouse/domain/entities/*`). JSON naming follows the
 * backend: camelCase with `ID` suffixes, quantities as numbers, dates as ISO
 * strings (`YYYY-MM-DD` for calendar days).
 */

/* ------------------------------- enums ------------------------------- */

export type OutboundOrderStatus =
  | 'OPEN'
  | 'PREPARING'
  | 'READY_TO_PICKUP'
  | 'SHIPPED'
  | 'IN_DELIVERY'
  | 'DELIVERED'
  | 'FAILED'
  | 'CANCELLED';

export type OutboundOrderSource = 'MANUAL' | 'CSV' | 'CLIENT_API';
/** TRANSFER ships to another hub; its shipping submit births a TRANSFER PO there. */
export type OutboundOrderType = 'REGULAR' | 'TRANSFER';
export type OutboundServiceType = 'SAME_DAY' | 'NEXT_DAY';
/** Backend OutboundMethod enum (delivery only for now). */
export type OutboundMethod = 'DELIVERY';
export type OutboundOrderEventOrigin = 'WMS' | 'FULFILLMENT' | 'CLIENT';

export type FulfillmentStatus =
  | 'AT_HUB'
  | 'DISPATCHED'
  | 'IN_DELIVERY'
  | 'DELIVERED'
  | 'FAILED'
  | 'RETURNED'
  | 'CANCELLED';

/**
 * Who carries the goods out, chosen at Submit shipping: DASH = a shipment in
 * Shipments (status flows back by event); LION_PARCEL = a third-party
 * carrier — the AWB is typed and the status is set by hand.
 */
export type FulfillmentCarrier = 'DASH' | 'LION_PARCEL' | 'HANDOVER';
/** Statuses ops may set by hand on a manually carried order. */
export const MANUAL_FULFILLMENT_STATUSES = [
  'IN_DELIVERY',
  'DELIVERED',
  'FAILED',
] as const satisfies readonly FulfillmentStatus[];
export type ManualFulfillmentStatus =
  (typeof MANUAL_FULFILLMENT_STATUSES)[number];

export type GoodsIssueStatus =
  'RESERVED' | 'PICKED' | 'READY_TO_PICKUP' | 'SHIPPED' | 'CANCELLED';

export type GoodsIssueStage = 'PICKING' | 'SHIPPING';

/**
 * Shipping view status: the GI status until SHIPPED, then the order's
 * delivery state (goods-issue.assembler).
 */
export type ShippingStatus =
  | GoodsIssueStatus
  | Extract<OutboundOrderStatus, 'IN_DELIVERY' | 'DELIVERED' | 'FAILED'>;

/**
 * GI list / summary status key: the GI status (picking) or the shipping
 * view status (shipping).
 */
export type GoodsIssueListStatus = ShippingStatus;

export type GoodsReceiptStatus = 'DRAFT' | 'POSTED';

export type PurchaseOrderStatus = 'DRAFT' | 'CONFIRMED' | 'CANCELLED';
/**
 * REGULAR = from a vendor, RETURN = goods sent back (free-text "diterima
 * dari"), TRANSFER = from another hub — created automatically, never by hand.
 */
export type PurchaseOrderType = 'REGULAR' | 'TRANSFER' | 'RETURN';
/** The types ops can create from the PO form. */
export type ManualPurchaseOrderType = Exclude<PurchaseOrderType, 'TRANSFER'>;

export type VendorStatus = 'ACTIVE' | 'INACTIVE';
/** INACTIVE SKUs can't go on a new PO / outbound order (422 SKU_INACTIVE). */
export type SkuStatus = 'ACTIVE' | 'INACTIVE';
export type PurchaseOrderReceiptStatus =
  'SCHEDULED' | 'PARTIAL_RECEIPT' | 'FINISHED';

export type StorageCategory = 'LOCATION' | 'SECTION' | 'BIN';
export type StorageType = 'STORAGE' | 'QUARANTINE' | 'STAGING';
export type StorageStatus = 'ACTIVE' | 'INACTIVE';

export type StockMovementType =
  'INBOUND' | 'OUTBOUND' | 'TRANSFER' | 'ADJUSTMENT';
export type StockMovementRefType =
  'GOODS_RECEIPT' | 'GOODS_ISSUE' | 'TRANSFER' | 'STOCK_OPNAME';

/** Fixed UoM list the SKU catalog validates against (no free text). Count
 *  units first, then weight/volume (chemicals — paint, thinner, solvents —
 *  come per kaleng/galon/jerigen/drum or by gram/ml). Same list as the server. */
export const WAREHOUSE_UOMS = [
  'pcs',
  'set',
  'lusin',
  'pack',
  'box',
  'carton',
  'sack',
  'bag',
  'roll',
  'pallet',
  'botol',
  'kaleng',
  'tube',
  'galon',
  'jerigen',
  'drum',
  'kg',
  'gram',
  'liter',
  'ml',
  'meter',
] as const;

/* ------------------------------ shared ------------------------------- */

/** Frozen hub snapshot. `code` is null on v1 rows. */
export interface HubRef {
  id: number;
  name: string;
  code: string | null;
}

/** Hub snapshot on transfer documents (source / destination hub). */
export interface TransferHubRef {
  id: number;
  name: string;
  code?: string | null;
}

/** Another document of the transfer pair (outbound order ↔ TRANSFER PO). */
export interface DocumentRef {
  id: string;
  code: string;
}

/** Frozen client snapshot. */
export interface ClientRef {
  id: number;
  name: string;
  /** The client's short code (provider `code`) — the `{CLIENT}` part of document codes. */
  code?: string | null;
}

export interface Pagination {
  size: number;
  page: number;
  lastPage: number;
  total: number;
}

export interface Paginated<T> {
  data: T[];
  pagination: Pagination;
}

export interface PageQuery {
  page?: number;
  limit?: number;
}

/** Created-date range: `YYYY-MM-DD`, WIB calendar days, inclusive. */
export interface CreatedRangeQuery {
  dateFrom?: string | null;
  dateTo?: string | null;
}

/** Non-blocking warning: requested qty per SKU exceeds available. */
export interface StockWarning {
  sku: string;
  requested: number;
  available: number;
}

/** One field-level validation problem (form, CSV, API). */
export interface FieldError {
  field: string;
  message: string;
  /** 0-based line index when the error belongs to a line. */
  lineIndex?: number | null;
}

/** INSUFFICIENT_STOCK detail row. */
export interface ShortfallRow {
  sku: string;
  requested: number;
  available: number;
  shortfall: number;
}

/* ------------------------------ outbound ------------------------------ */

export interface OutboundOrderGoodsIssueRef {
  id: string;
  code: string;
  status: GoodsIssueStatus;
  suratJalanNo: string | null;
  pickerName: string | null;
}

export interface OutboundOrderFulfillment {
  /** null while not shipped, or when the shipments adapter is disabled. */
  status: FulfillmentStatus | null;
  /** DASH (Shipments) or LION_PARCEL (manual); null until shipped. */
  carrier?: FulfillmentCarrier | null;
  /** The Shipments row — DASH only. */
  shipmentID: string | null;
  waybill: string | null;
}

/** One label line — what the Dash shipment item would carry. */
export interface OutboundOrderShippingLabelItem {
  /** Printed as the item name, like a Dash shipment item. */
  sku: string;
  itemName: string;
  /** Surat jalan no, else the order code. */
  invoice: string;
  /** Parcels the line ships as (= koli). */
  quantity: number;
  koli: number;
  /** kg; 0 when the SKU has no weight. */
  weight: number;
}

/** POST /outbound-orders/shipping-labels/bulk — an order a 3PL took (no shipment). */
export interface OutboundOrderShippingLabel {
  outboundOrderID: string;
  code: string;
  carrier: FulfillmentCarrier;
  /** The AWB WMS issued at submit shipping (`DLEXT…`). */
  waybill: string;
  bookingID: string;
  client: ClientRef | null;
  senderName: string;
  senderAddress: string | null;
  recipientName: string;
  recipientAddress: string;
  items: OutboundOrderShippingLabelItem[];
}

/** POST /outbound-orders/:id/fulfillment-status — manual carrier progress. */
export interface UpdateFulfillmentPayload {
  status: ManualFulfillmentStatus;
  note?: string | null;
}

/** GET /outbound-orders/external-id — is this client ref already an order? */
export interface ExternalIdCheck {
  exists: boolean;
  order: { id: string; code: string; status: OutboundOrderStatus } | null;
}

export interface OutboundOrderUploadRef {
  id: string;
  code: string;
  fileName: string;
}

/** The rider carrying a shipped order (Dash shipments only): read-time
 *  snapshot from its shipment, so a reassignment shows up at once. */
export interface OutboundOrderCourier {
  id: number;
  /** Mitra code, e.g. MTR0006903. */
  code: string;
  name: string;
  phone: string | null;
  uuid: string | null;
  profileImageUrl: string | null;
}

export interface OutboundOrder {
  id: string;
  code: string;
  source: OutboundOrderSource;
  externalID: string;
  externalIDAuto: boolean;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  client: ClientRef | null;
  type: OutboundOrderType;
  /** TRANSFER: the hub the goods go to. */
  destinationHub: TransferHubRef | null;
  /** TRANSFER: the PO born at the destination hub, set once shipping is submitted. */
  transferPurchaseOrder: DocumentRef | null;
  /** TRANSFER raised by a transfer warehouse: that document (`TRF-EXT-…`). */
  hubTransfer?: DocumentRef | null;
  status: OutboundOrderStatus;
  serviceType: OutboundServiceType;
  method: OutboundMethod;
  recipientName: string;
  recipientPhone: string;
  destinationAddress: string;
  destinationLatitude: number;
  destinationLongitude: number;
  destinationNotes: string;
  /** Destination city — always a core-service master city (snapshot name +
   *  id); both null when none matched and ops has not picked one yet. */
  destinationCity: string | null;
  destinationCityID: number | null;
  /** Hub → destination road distance in metres (generated, or typed on a
   *  CSV row); null on orders from before it was stored. */
  distance: number | null;
  notes: string;
  orderDate: string;
  /** YYYY-MM-DD */
  expectedDeliveryDate: string;
  isSandbox: boolean;
  lineCount: number;
  totalQty: number;
  goodsIssue: OutboundOrderGoodsIssueRef | null;
  fulfillment: OutboundOrderFulfillment;
  /** null until a rider is assigned; always null for a third-party carrier. */
  courierID: number | null;
  courier: OutboundOrderCourier | null;
  upload: OutboundOrderUploadRef | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface OutboundOrderLine {
  id: string;
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
  qtyReserved: number;
  qtyPicked: number;
  /** Allocatable qty in the hub right now (warning aid, not a reserve). */
  qtyAvailable: number;
  notes: string;
}

export interface OutboundOrderEvent {
  id: string;
  status: OutboundOrderStatus | null;
  title: string;
  note: string;
  actor: string;
  origin: OutboundOrderEventOrigin;
  occurredAt: string;
}

export interface OutboundOrderDetail {
  order: OutboundOrder;
  lines: OutboundOrderLine[];
  /** Newest first. */
  events: OutboundOrderEvent[];
}

export interface OutboundOrderSummary {
  total: number;
  byStatus: { status: OutboundOrderStatus; count: number }[];
}

/** GET /outbound-orders/summary — tab badges (the list's filters minus status). */
export interface OutboundOrderSummaryQuery extends CreatedRangeQuery {
  hubID?: number | null;
  clientID?: number | null;
  source?: OutboundOrderSource | null;
  type?: OutboundOrderType | null;
  method?: OutboundMethod | null;
  uploadID?: string | null;
  search?: string | null;
  /** expectedDeliveryDate range (WIB days). */
  deliveryFrom?: string | null;
  deliveryTo?: string | null;
  /** A core city id, or `none` = orders whose city is still unresolved. */
  destinationCityID?: string | null;
}

export interface ListOutboundOrdersQuery
  extends PageQuery, OutboundOrderSummaryQuery {
  status?: OutboundOrderStatus | null;
}

/** GET /outbound-orders/export — the list's filters + tab, every page (no paging). */
export interface ExportOutboundOrdersQuery extends OutboundOrderSummaryQuery {
  status?: OutboundOrderStatus | null;
}

/** Snapshot fields every hub/client-scoped create carries (TRD § Architecture). */
export interface HubClientSnapshot {
  hubID: number;
  hubName: string;
  hubCode: string | null;
  clientID: number;
  clientName: string;
  /** The client's provider code, trimmed; left out when it has none (the server falls back to `C{clientID}`). */
  clientCode?: string;
}

export interface CreateOutboundOrderPayload extends HubClientSnapshot {
  /** Default REGULAR. TRANSFER needs the destination hub (≠ hubID). */
  type?: OutboundOrderType;
  destinationHubID?: number;
  destinationHubName?: string;
  destinationHubCode?: string;
  /** Optional for manual orders — the backend defaults it to the OO code. */
  externalID?: string;
  serviceType: OutboundServiceType;
  /** YYYY-MM-DD, Asia/Jakarta calendar day. */
  expectedDeliveryDate: string;
  recipient: { name: string; phone: string };
  destination: {
    address: string;
    latitude: number | null;
    longitude: number | null;
    notes: string;
    /** Core city picked (or confirmed) in the form; `null` = none; absent →
     *  the server resolves it from the address / point. */
    cityID?: number | null;
  };
  lines: { sku: string; qty: number | null }[];
  notes: string;
}

/** One row of core-service's city master (`GET /cities`). */
export interface CityOption {
  id: number;
  name: string;
  provinceID: number | null;
}

export interface CityRef {
  id: number;
  name: string;
}

/** `GET /outbound-orders/resolve-city` — the city the server would store. */
export interface ResolveCityResult {
  city: CityRef | null;
  source: 'ADDRESS' | 'PICKED' | null;
  warning: string | null;
}

/** "Ubah" outbound request: recipient + destination (rules as create), and
 *  — only while OPEN, before picking — the hub (sent with its snapshot). */
export interface UpdateOutboundOrderPayload {
  hubID?: number;
  hubName?: string;
  hubCode?: string | null;
  recipient: CreateOutboundOrderPayload['recipient'];
  destination: CreateOutboundOrderPayload['destination'] & {
    /** Metres typed by hand (the generated estimate was off); omitted →
     *  the server regenerates it when the point moved. */
    distance?: number;
  };
}

export interface CreateOutboundOrderResult {
  result: 'CREATED' | 'EXISTS';
  order: OutboundOrder;
  stockWarnings: StockWarning[];
}

export interface CancelOutboundOrderResult {
  order: OutboundOrder;
  releasedGoodsIssueCode: string | null;
  /** Cancelled after submit shipping: the goods put back on hand. */
  restocked?: { goodsIssueCode: string; lines: number; totalQty: number } | null;
}

export interface OutboundOrderUpload {
  id: string;
  code: string;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  client: ClientRef | null;
  fileName: string;
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  createdBy: string;
  createdAt: string;
}

/* -------------------------------- CSV -------------------------------- */

export interface CsvImportSummary {
  rows: number;
  orders: number;
  toCreate: number;
  existing: number;
  errors: number;
  warnings: number;
}

export interface CsvImportOrderRow {
  externalID: string;
  /** 1-based CSV row numbers (header = row 1). */
  rows: number[];
  recipientName: string;
  destinationAddress: string;
  /** Hub → destination distance (metres) the order will carry; null when
   *  not computed (existing / invalid order). */
  distance: number | null;
  lineCount: number;
  totalQty: number;
  status: 'NEW' | 'EXISTS' | 'ERROR';
  existingCode: string | null;
  errorCount: number;
}

export interface CsvImportError {
  row: number;
  field: string;
  value: string;
  message: string;
}

export interface CsvDryRunResult {
  summary: CsvImportSummary;
  ignoredColumns: string[];
  orders: CsvImportOrderRow[];
  errors: CsvImportError[];
  warnings: StockWarning[];
}

export interface CsvCommitResult {
  upload: OutboundOrderUpload;
  orderIDs: string[];
}

/* ---------------------- outbound destination (address) ------------------ */

/** GET /v1/addresses/destination-config?clientId= */
export interface DestinationConfig {
  /** true = consumer delivery (address → geocode); false = the client's places. */
  destinationToClient: boolean;
}

/** GET /v1/addresses/geocode?address= (cache-first). */
export interface GeocodeResult {
  /** Formatted address that matched. */
  address: string;
  latitude: number;
  longitude: number;
  source: 'CACHE' | 'GOOGLE';
}

/** GET /v1/addresses/places — a known place of the client. */
export interface DestinationPlace {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  endpoint?: string;
  clientId?: number | null;
}

/* ---------------------------- goods issues ---------------------------- */

/** The goods issue's outbound order, as seen from GI lists (null for v1 GIs). */
export interface GoodsIssueOrderRef {
  id: string;
  code: string;
  externalID: string;
  recipientName: string;
  /** YYYY-MM-DD */
  expectedDeliveryDate: string;
  method: OutboundMethod;
  status: OutboundOrderStatus;
}

export interface GoodsIssueSummary {
  id: string;
  code: string;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  client: ClientRef | null;
  reference: string;
  status: GoodsIssueStatus;
  pickerName: string | null;
  suratJalanNo: string | null;
  lineCount: number;
  totalReserved: number;
  totalPicked: number;
  outboundOrderID: string | null;
  outboundOrder: GoodsIssueOrderRef | null;
  /** Shipping view status: GI status until SHIPPED, then the order's. */
  shippingStatus: ShippingStatus;
  fulfillmentStatus: FulfillmentStatus | null;
  /** DASH / LION_PARCEL once shipped (the order's carrier). */
  carrier?: FulfillmentCarrier | null;
  waybill: string | null;
  shipmentID: string | null;
  readyAt: string | null;
  readyBy: string | null;
  pickedAt: string | null;
  shippedAt: string | null;
  shippedBy: string | null;
  cancelledAt: string | null;
  createdBy: string;
  createdAt: string;
  /**
   * Optimistic version token of the goods issue header (ISO). The server
   * bumps it on every mutation (lines rewrite, picker change, ready to
   * pick up); sent back as `expectedUpdatedAt` it makes a write against a
   * newer version answer 409 GOODS_ISSUE_STALE instead of overwriting it.
   */
  updatedAt: string;
}

export interface GoodsIssueLine {
  id: string;
  stockID: string;
  storageID: string;
  /** The holding storage's own name. */
  storageName: string;
  /** "Location name / Section name / Bin name". */
  storagePath: string;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  qtyReserved: number;
  qtyPicked: number;
  /** Per-unit weight from the SKU catalog; null = not set on the SKU. */
  weightKg?: number | null;
}

/**
 * GET /goods-issues/:id/sources?sku= — one stock row (location + batch) the
 * goods issue can reserve that SKU from: same hub, client and SKU; an ACTIVE
 * storage under a STORAGE-type location; available > 0; FEFO order (expiry
 * asc, nulls last, deeper storage first). Rows this goods issue already holds
 * are listed too — their hold counts as available.
 */
export interface GoodsIssueSource {
  stockID: string;
  storageID: string;
  /** The holding storage's own name. */
  storageName: string;
  storageCategory: StorageCategory;
  /** "Location / Section / Bin" (names). */
  storagePath: string;
  batchNumber: string;
  /** YYYY-MM-DD */
  expiredDate: string | null;
  /** Informational: picking takes both types (FEFO). Absent when the server does not send it. */
  stockType?: WarehouseStockType;
  /** Free qty on the row + what this goods issue already holds there. */
  available: number;
  /** This goods issue's current reservation on the row (0 if none). */
  heldByThisIssue: number;
}

/**
 * One holding storage of `GET /goods-issues/:id/sources/storages` — a
 * location the SKU can be reserved from (same allocatable rule as the stock
 * rows), with the sum over its batches.
 */
export interface GoodsIssueSourceStorage {
  storageID: string;
  storageName: string;
  /** "Location / Section / Bin" (names). */
  storagePath: string;
  storageCategory: StorageCategory;
  /** Type of the holder's location; null when the server doesn't know it. */
  storageType: StorageType | null;
  /** Free qty over its batches (this goods issue's hold included). */
  available: number;
  batchCount: number;
}

/** `GET /goods-issues/:id/sources/storages` — `page` sent → paged. */
export interface GoodsIssueSourceStoragesQuery extends PageQuery {
  sku: string;
  /** Name path (ILIKE). */
  search?: string | null;
}

/** `GET /goods-issues/:id/sources` — `page` sent → paged. */
export interface GoodsIssueSourcesQuery extends PageQuery {
  sku: string;
  /** Only the stock rows held in this storage. */
  storageID?: string | null;
  /** Batch number or name path (ILIKE). */
  search?: string | null;
}

/** One reservation row of PUT /goods-issues/:id/lines. */
export interface GoodsIssueLineInput {
  sku: string;
  stockID: string;
  qty: number;
}

/**
 * PUT /goods-issues/:id/lines — replaces the goods issue's whole reservation
 * list (1–200 rows; per-SKU totals must equal the order). 422 field errors
 * name `lines[i].sku|stockID|qty`.
 */
export interface UpdateGoodsIssueLinesPayload {
  lines: GoodsIssueLineInput[];
  /** The `updatedAt` the caller loaded; a newer one answers 409 GOODS_ISSUE_STALE. */
  expectedUpdatedAt?: string;
}

export interface GoodsIssueDetail {
  goodsIssue: GoodsIssueSummary;
  /** Ordered by bin path (pick walk order). */
  lines: GoodsIssueLine[];
  /** null for standalone v1 goods issues. */
  outboundOrder: OutboundOrder | null;
}

/** GET /goods-issues/summary — the list's filters minus status. */
export interface GoodsIssueSummaryQuery extends CreatedRangeQuery {
  hubID?: number | null;
  stage: GoodsIssueStage;
  clientID?: number | null;
  /** pickerName ILIKE. */
  picker?: string | null;
  search?: string | null;
}

export interface ListGoodsIssuesQuery extends PageQuery, CreatedRangeQuery {
  hubID?: number | null;
  stage?: GoodsIssueStage | null;
  clientID?: number | null;
  /** pickerName ILIKE. */
  picker?: string | null;
  /**
   * PICKING: RESERVED | PICKED (GI status).
   * SHIPPING: READY_TO_PICKUP | SHIPPED | IN_DELIVERY | DELIVERED | FAILED
   * (shippingStatus; SHIPPED = shipped but the order is not in delivery yet).
   */
  status?: GoodsIssueListStatus | null;
  search?: string | null;
}

/**
 * GET /goods-issues/summary — tab badges per stage. `byStatus` keys:
 * PICKING = RESERVED (Not picked) · PICKED; SHIPPING = its shipping tabs.
 */
export interface GoodsIssueStageSummary {
  total: number;
  byStatus: { key: GoodsIssueListStatus; count: number }[];
}

/**
 * A bulk item that failed. `error` is the machine code: a domain code
 * (INSUFFICIENT_STOCK, INVALID_STATUS_TRANSITION, …), INTERNAL (unexpected,
 * nothing changed for that item) or RETRYABLE (transient lock / deadlock —
 * safe to send that item again).
 */
export interface BulkItemError {
  error: string;
  message: string;
  /** INSUFFICIENT_STOCK detail: per-SKU shortfall. */
  errors: unknown[];
}

export interface BulkPickingResult {
  outboundOrderID: string;
  ok: boolean;
  goodsIssueID: string | null;
  goodsIssueCode?: string | null;
  error: BulkItemError | null;
}

/** POST /outbound-orders/fulfillment-status/bulk — per order. */
export interface BulkFulfillmentResult {
  outboundOrderID: string;
  ok: boolean;
  /** The order's status after the update (null when it failed). */
  status: OutboundOrderStatus | null;
  error: BulkItemError | null;
}

export interface BulkShipResult {
  goodsIssueID: string;
  ok: boolean;
  waybill: string | null;
  error: BulkItemError | null;
}

export interface SubmitShippingResult {
  detail: GoodsIssueDetail;
  /** null for a standalone goods issue (stock still deducted). */
  fulfillment: {
    status: FulfillmentStatus;
    carrier: FulfillmentCarrier;
    /** The Shipments row — DASH only. */
    shipmentID: string | null;
    /** DASH: the AWB Shipments issued; manual: the one WMS issued. */
    waybill: string | null;
  } | null;
}

/** Body of POST /goods-issues/:id/ship (DASH when omitted). */
export interface SubmitShippingPayload {
  carrier?: FulfillmentCarrier;
  /** HANDOVER: who received the goods (required for HANDOVER). */
  handoverRecipientName?: string;
  handoverNote?: string;
}

/* ---------------------- purchase orders & receipts --------------------- */

export interface PurchaseOrderLine {
  id: string;
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
  qtyReceived: number;
  qtyRemaining: number;
}

export interface PurchaseOrder {
  id: string;
  code: string;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  client: ClientRef | null;
  type: PurchaseOrderType;
  /** REGULAR: the vendor the goods come from. */
  vendor: VendorRef | null;
  /** TRANSFER: the hub the goods come from. */
  sourceHub: TransferHubRef | null;
  /** TRANSFER: the outbound order at the source hub. */
  transferOutboundOrder: DocumentRef | null;
  /** TRANSFER from a transfer warehouse: that document (`TRF-EXT-…`). */
  hubTransfer?: DocumentRef | null;
  /** Display name of the source for every type (vendor / source hub / returned from). */
  supplierName: string;
  estArrivalAt: string | null;
  notes: string;
  status: PurchaseOrderStatus;
  /** Inbound request progress; null while DRAFT/CANCELLED. */
  receiptStatus: PurchaseOrderReceiptStatus | null;
  lineCount: number;
  totalQty: number;
  totalReceived: number;
  confirmedAt: string | null;
  confirmedBy: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface GoodsReceiptSummary {
  id: string;
  code: string;
  hubID: number;
  hub: HubRef | null;
  clientID: number;
  client: ClientRef | null;
  purchaseOrderID: string | null;
  purchaseOrderCode: string | null;
  supplierName: string;
  status: GoodsReceiptStatus;
  arrivedAt: string | null;
  deliveryNoteNo: string | null;
  lineCount: number;
  totalQty: number;
  postedAt: string | null;
  postedBy: string | null;
  createdBy: string;
  createdAt: string;
}

export interface PurchaseOrderDetail {
  order: PurchaseOrder;
  lines: PurchaseOrderLine[];
  goodsReceipts: GoodsReceiptSummary[];
}

export interface GoodsReceiptLine {
  id: string;
  purchaseOrderLineID: string | null;
  storageID: string;
  /** The destination storage's own name. */
  storageName: string;
  /** "Location / Section / Bin" (names). */
  storagePath: string;
  sku: string;
  itemName: string;
  uomName: string;
  qty: number;
  expiredDate: string | null;
  batchNumber: string | null;
}

export interface GoodsReceiptDetail {
  receipt: GoodsReceiptSummary;
  lines: GoodsReceiptLine[];
  deliveryNotePhotoUrl: string | null;
  closesBackorder: boolean;
  notes: string;
}

/** PUT /purchase-orders/:id — edit a DRAFT PO (hub, client, type stay). */
export interface UpdateDraftPurchaseOrderPayload {
  vendorID?: string;
  returnedFrom?: string;
  estArrivalAt?: string | null;
  notes?: string;
  lines: { sku: string; qty: number }[];
}

/**
 * Manual PO. REGULAR needs an ACTIVE `vendorID`, RETURN a `returnedFrom`;
 * the server derives `supplierName` from either.
 */
export interface CreatePurchaseOrderPayload extends HubClientSnapshot {
  type: ManualPurchaseOrderType;
  vendorID?: string;
  returnedFrom?: string;
  /** ISO datetime; null = unknown. */
  estArrivalAt: string | null;
  notes: string;
  lines: { sku: string; qty: number }[];
}

export interface CreateInboundReceiptPayload {
  arrivedAt: string | null;
  deliveryNoteNo: string | null;
  deliveryNotePhotoUrl: string | null;
  closesBackorder: boolean;
  notes: string;
  lines: {
    sku: string;
    qty: number;
    /** YYYY-MM-DD, required on every line — the server builds the batch number from it and the GR code. */
    /** YYYY-MM-DD, or null for goods without an expiry (NOEXP batch). */
    expiredDate: string | null;
    storageID: string;
  }[];
}

/** PO / inbound filters shared by the lists and their summaries. */
export interface PurchaseOrderFilterQuery extends CreatedRangeQuery {
  hubID?: number | null;
  clientID?: number | null;
  type?: PurchaseOrderType | null;
  vendorID?: string | null;
  search?: string | null;
  /** estArrivalAt range (WIB days). */
  arrivalFrom?: string | null;
  arrivalTo?: string | null;
}

export interface ListPurchaseOrdersQuery
  extends PageQuery, PurchaseOrderFilterQuery {
  status?: PurchaseOrderStatus | null;
}

export interface ListInboundRequestsQuery
  extends PageQuery, PurchaseOrderFilterQuery {
  receiptStatus?: PurchaseOrderReceiptStatus | null;
  /** Only requests still waiting for goods (SCHEDULED + PARTIAL_RECEIPT). */
  open?: boolean | null;
}

/** Tab badges of the PO list / inbound list: the list's filters minus status. */
export type PurchaseOrderSummaryQuery = PurchaseOrderFilterQuery;

/** GET /purchase-orders/summary — POs per status. */
export interface PurchaseOrderSummary {
  total: number;
  byStatus: { status: PurchaseOrderStatus; count: number }[];
}

/** GET /inbound-requests/summary — inbound requests per receipt status. */
export interface InboundRequestSummary {
  total: number;
  byStatus: { status: PurchaseOrderReceiptStatus; count: number }[];
}

/* ------------------------------- vendors ------------------------------- */

/** Global vendor master (not per hub / client). */
export interface Vendor {
  id: string;
  /**
   * "ID Vendor": typed by ops (upper-cased, [A-Z0-9._/-], min 2) or
   * VEN-{MMDDYY}-{RAND4} (mixed case) when left blank. Unique, case-insensitive.
   */
  code: string;
  /** "Vendor Name": unique, case-insensitive. */
  name: string;
  /** "Vendor Address". */
  address: string | null;
  /** "Vendor Phone". */
  phone: string | null;
  status: VendorStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Vendor as embedded in a PO. */
export interface VendorRef {
  id: string;
  code: string;
  name: string;
}

export interface ListVendorsQuery extends PageQuery {
  search?: string | null;
  status?: VendorStatus | null;
}

export interface CreateVendorPayload {
  /** Blank → generated by the server. */
  code?: string;
  name: string;
  address?: string;
  phone?: string;
}

/** PATCH body: only the changed fields; `null` clears an optional field. */
export interface UpdateVendorPayload {
  code?: string;
  name?: string;
  address?: string | null;
  phone?: string | null;
  status?: VendorStatus;
}

/** One CSV row of POST /vendors/import ("No" is dropped). */
export interface VendorRowPayload {
  code?: string;
  name: string;
  address?: string;
  phone?: string;
}

export interface ImportVendorsResult {
  created: number;
  skippedDuplicates: number;
}

export interface ListGoodsReceiptsQuery extends PageQuery {
  hubID?: number | null;
  purchaseOrderID?: string | null;
  clientID?: number | null;
  /** Via the receipt's PO. */
  vendorID?: string | null;
  search?: string | null;
  /** Receipt date (arrival / received, else created), WIB days. */
  dateFrom?: string | null;
  dateTo?: string | null;
}

/* -------------------------- stock & storage -------------------------- */

export interface StockSummary {
  skuCount: number;
  binsInUse: number;
  qtyOnHand: number;
  qtyReserved: number;
  qtyAvailable: number;
}

export interface StorageLineage {
  id: string;
  name: string;
  type?: StorageType;
}

export interface WarehouseStorage {
  id: string;
  hubID: number;
  hub: { id: number; name: string; code?: string | null } | null;
  name: string;
  category: StorageCategory;
  /** Set on LOCATION rows only; SECTION/BIN inherit their location's type. */
  type: StorageType | null;
  path: string;
  status: StorageStatus;
  location?: StorageLineage;
  section?: Omit<StorageLineage, 'type'>;
  /**
   * `withStock=true` only: on-hand qty in this storage AND everything under
   * it (rolled up by the server — stock can sit at any level).
   */
  qtyOnHand?: number;
  /** `withStock=true` only: on-hand qty held directly in this storage. */
  qtyOnHandDirect?: number;
  /* capacity (set on BIN rows; null = "belum diset", never 0) */
  widthCm?: number | null;
  depthCm?: number | null;
  heightCm?: number | null;
  capacityCbm?: number | null;
  capacityKg?: number | null;
  capacityQty?: number | null;
  /** `withStock=true` only: occupancy roll-up of this storage's subtree. */
  usedCbm?: number;
  usedKg?: number;
  pctCbm?: number | null;
  occupancyStatus?: StorageOccupancyStatus;
  createdAt: string;
  updatedAt?: string;
}

/* ----------------------- storage occupancy & layout ----------------------- */

/** Occupancy status per bin (thresholds on `usedCbm / capacityCbm`, computed by the server). */
export type StorageOccupancyStatus =
  | 'TERSEDIA'
  | 'TERISI'
  | 'HAMPIR_PENUH'
  | 'PENUH'
  | 'OVER'
  | 'KAPASITAS_BELUM_DIATUR';

export const STORAGE_OCCUPANCY_STATUSES: StorageOccupancyStatus[] = [
  'TERSEDIA',
  'TERISI',
  'HAMPIR_PENUH',
  'PENUH',
  'OVER',
  'KAPASITAS_BELUM_DIATUR',
];

export interface StorageOccupancyLine {
  clientID: number;
  client: { id: number; name: string; code?: string | null } | null;
  sku: string;
  itemName: string;
  uomName: string;
  qtyOnHand: number;
}

/** One BIN with its stock lines (`GET /storages/occupancy`). */
export interface StorageOccupancyRow {
  storageID: string;
  path: string;
  name: string;
  location: StorageLineage;
  section: Omit<StorageLineage, 'type'>;
  status: StorageOccupancyStatus;
  capacityCbm: number | null;
  capacityKg: number | null;
  capacityQty: number | null;
  usedCbm: number;
  usedKg: number;
  usedQty: number;
  pctCbm: number | null;
  /** A stock line's SKU has no dimensions — its m³ counts as 0. */
  hasNoDim: boolean;
  lines: StorageOccupancyLine[];
}

export interface StorageOccupancySummary {
  binActive: number;
  binUsed: number;
  binUnset: number;
  binHot: number;
  usedCbm: number;
  capacityCbm: number;
  pctCbm: number | null;
  statusCounts: Partial<Record<StorageOccupancyStatus, number>>;
}

export interface StorageOccupancyPage extends Paginated<StorageOccupancyRow> {
  summary: StorageOccupancySummary;
}

export interface StorageOccupancyFilterQuery {
  hubID: number;
  locationID?: string | null;
  sectionID?: string | null;
  status?: StorageOccupancyStatus[] | null;
  clientID?: number | null;
  /** Bin / section / location name or SKU / item name (ILIKE). */
  search?: string | null;
}

export interface ListStorageOccupancyQuery
  extends StorageOccupancyFilterQuery, PageQuery {}

export interface StorageOccupancyBoardSection {
  sectionID: string;
  name: string;
  binCount: number;
  binUsed: number;
  binAvailable: number;
  pctCbm: number | null;
  status: StorageOccupancyStatus;
}

/** One card per location (`GET /storages/occupancy/board`). */
export interface StorageOccupancyBoardLocation {
  locationID: string;
  name: string;
  type: StorageType;
  status: StorageOccupancyStatus;
  pctCbm: number | null;
  binCount: number;
  binUsed: number;
  binUnset: number;
  usedCbm: number;
  capacityCbm: number;
  /** Every section of the location; `sections` holds at most 8 (fullest first). */
  sectionCount: number;
  sections: StorageOccupancyBoardSection[];
}

export type StorageOccupancyBoardSort = 'hot' | 'name';

/** `GET /storages/occupancy/board` — paginated cards (limit ≤ 48). */
export interface StorageOccupancyBoardQuery extends PageQuery {
  hubID: number;
  clientID?: number | null;
  /** Location name OR any of its section names (ILIKE). */
  search?: string | null;
  /** `hot` (default): fullest first; `name`: tree order by name. */
  sort?: StorageOccupancyBoardSort;
}

/** A level of the storage tree in the occupancy drill-down. */
export type StorageOccupancyChildCategory = 'LOCATION' | 'SECTION' | 'BIN';

/** A LOCATION / SECTION the drill-down stands in (or one of its ancestors). */
export interface StorageOccupancyNode {
  storageID: string;
  name: string;
  category: 'LOCATION' | 'SECTION';
}

/** The level being listed, with its lineage from the top (`[location]` for a SECTION). */
export interface StorageOccupancyChildrenParent extends StorageOccupancyNode {
  ancestors: StorageOccupancyNode[];
}

/**
 * One child of the drill-down (`GET /storages/occupancy/children?basis=`): a
 * SECTION of a location or a BIN of a section, with its utilization on the
 * requested basis (BE doc § 7.3). `usedQty` = units held, whatever the basis.
 */
export interface StorageOccupancyChild extends UtilizationFigures {
  storageID: string;
  name: string;
  category: StorageOccupancyChildCategory;
  usedQty: number;
}

/** `GET /storages/occupancy/children` — one paged level (limit ≤ 48). */
export interface StorageOccupancyChildrenQuery extends PageQuery {
  hubID: number;
  /** Absent: the hub's LOCATIONs; a LOCATION: its SECTIONs; a SECTION: its BINs. */
  parentID?: string | null;
  /** That client's usage only. */
  clientID?: number | null;
  /** Child name (at the top level also any section name under the location). */
  search?: string | null;
  /** `hot` (default): fullest first; `name`: tree order. */
  sort?: StorageOccupancyBoardSort;
  /** What the figures are measured in (default `volume`). */
  basis?: UtilizationBasis;
}

export interface StorageOccupancyChildren extends Paginated<StorageOccupancyChild> {
  /** `null` at the top level (the hub's locations). */
  parent: StorageOccupancyChildrenParent | null;
}

/* ------------------- location utilization (BE doc § 7) ------------------- */

/** What utilization is measured in: m³, kg or units (qty). */
export type UtilizationBasis = 'volume' | 'weight' | 'unit';

/**
 * Status of a location / section / bin on the chosen basis, thresholds from
 * the payload: `KAPASITAS_BELUM_DIATUR` (no capacity on that basis),
 * `KOSONG` (nothing used), `NORMAL` (< warn), `HAMPIR_PENUH` (≥ warn),
 * `PENUH` (≥ full, also over 100 %).
 */
export type ZoneOccupancyStatus =
  'KAPASITAS_BELUM_DIATUR' | 'KOSONG' | 'NORMAL' | 'HAMPIR_PENUH' | 'PENUH';

/** Percent thresholds of the status (server constants, never hardcoded in the FE). */
export interface ZoneOccupancyThresholds {
  warn: number;
  full: number;
}

/** The figures every level carries for the requested basis (BE doc § 7.1). */
export interface UtilizationFigures {
  /** Usage of the rows that have a capacity on the basis. */
  used: number;
  /** Σ capacity of the rows that have one; 0 = not set anywhere. */
  capacity: number;
  /** `used ÷ capacity × 100` (1 decimal); null when `capacity` is 0. */
  pct: number | null;
  status: ZoneOccupancyStatus;
  /** max(0, capacity − used). */
  free: number;
  binCount: number;
  /** Active bins without a capacity on the basis (left out of the numbers). */
  binsWithoutCapacity: number;
  /** Usage held in rows without a capacity on the basis. */
  usedWithoutCapacity: number;
  /** Volume only: a SKU without dimensions in the subtree (its m³ counts 0). */
  hasNoDim: boolean;
}

/** The hub roll-up over its STORAGE-type locations. */
export interface LocationUtilizationHub extends UtilizationFigures {
  locationCount: number;
}

/** One active location of the hub (any type). */
export interface LocationUtilization extends UtilizationFigures {
  storageID: string;
  name: string;
  type: StorageType;
  sectionCount: number;
}

/** A storage location at or over the warn threshold, with where to move stock to. */
export interface LocationUtilizationAttention {
  storageID: string;
  name: string;
  pct: number | null;
  free: number;
  status: ZoneOccupancyStatus;
  /** The storage location with the most free room (pct < warn); null when none. */
  moveTo: { storageID: string; name: string; free: number } | null;
}

/** A non-storage location (staging / quarantine) left out of the hub numbers. */
export interface LocationUtilizationExcluded {
  storageID: string;
  name: string;
  type: StorageType;
}

/** `GET /storages/occupancy/locations?hubID&clientID?&basis&search?&minPct?&maxPct?&page&limit&attentionPage&attentionLimit` */
export interface LocationUtilizationPayload {
  basis: UtilizationBasis;
  unit: 'm³' | 'kg' | 'unit';
  thresholds: ZoneOccupancyThresholds;
  /** Every storage location of the hub (not narrowed by search / filter / page). */
  hub: LocationUtilizationHub;
  /** ONE page of the matching locations, fullest first (null pct last). */
  locations: LocationUtilization[];
  /** Of the matching locations, not of the hub. */
  pagination: Pagination;
  /** ONE page of the storage locations to act on, fullest first (hub-wide). */
  attention: LocationUtilizationAttention[];
  /** Of every storage location to act on (`attentionPage` / `attentionLimit`). */
  attentionPagination: Pagination;
  excluded: LocationUtilizationExcluded[];
}

export interface LocationUtilizationQuery {
  hubID: number;
  /** `used` counts that client's stock only; capacity is unchanged. */
  clientID?: number | null;
  basis: UtilizationBasis;
  /** Location name contains (server-side); hub / attention stay hub-wide. */
  search?: string | null;
  /** Inclusive pct bounds; rows without capacity drop out when either is set. */
  minPct?: number | null;
  maxPct?: number | null;
  page?: number;
  /** Locations per page: 12 by default, 48 at most. */
  limit?: number;
  /** Page of "Perlu tindakan" (1 by default). */
  attentionPage?: number;
  /** Locations to act on per page: 5 by default, 48 at most. */
  attentionLimit?: number;
}

export interface CapacityImportResult {
  totalData: number;
  totalSuccess: number;
  totalFail: number;
  messages: string[];
  /** One entry per problem (row · CSV column · cell · why). Absent on older servers: use `messages`. */
  errors?: CsvImportError[];
}

/** A rectangle on the 50 cm grid, relative to its row's origin. */
export interface LayoutBlock {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type StorageLayoutShape = 'RACK' | 'FLOOR';

/** Position of one storage on the map (`wh_storage_layouts`). */
export interface StorageLayoutRow {
  storageID: string;
  category?: StorageCategory;
  /** LOCATION: from hub origin. SECTION: from its location origin. cm. */
  x: number;
  y: number;
  /** LOCATION only: bounding box of `blocks`. */
  w?: number | null;
  h?: number | null;
  /** LOCATION only: null = one `w × h` rectangle; 2–4 blocks = L/U/W. */
  blocks?: LayoutBlock[] | null;
  rotation?: 0 | 90;
  shape?: StorageLayoutShape | null;
  levels?: number | null;
  rows?: number | null;
  cellCm?: number | null;
}

export interface StorageFloor {
  w: number;
  h: number;
  blocks: LayoutBlock[];
  /** Optimistic-lock token; send it back on save (409 LAYOUT_STALE otherwise). */
  version: number;
}

export interface StorageLayout {
  floor: StorageFloor | null;
  layouts: StorageLayoutRow[];
}

export interface SaveStorageLayoutPayload {
  hubID: number;
  version?: number;
  floor: { blocks: LayoutBlock[] };
  layouts: StorageLayoutRow[];
}

export interface SaveStorageLayoutResult {
  floor: StorageFloor;
  placed: number;
}

/** Map status codes (`WarehouseMapPayload.status`) → status names. */
export const MAP_STATUS_CODES: StorageOccupancyStatus[] = [
  'TERSEDIA',
  'TERISI',
  'HAMPIR_PENUH',
  'PENUH',
  'OVER',
  'KAPASITAS_BELUM_DIATUR',
];

/**
 * `GET /storages/map` — columnar: one entry per storage of the hub in tree
 * order, arrays indexed alike. Kept as typed arrays in a ref by the renderer.
 */
export interface WarehouseMapPayload {
  hubID: number;
  etag: string;
  floor: StorageFloor | null;
  n: number;
  id: string[];
  name: string[];
  path: string[];
  /** 1 LOCATION, 2 SECTION, 3 BIN */
  category: number[];
  type: (StorageType | null)[];
  /** Index of the parent row, -1 for a LOCATION. */
  parent: number[];
  active: number[];
  /** Index into `MAP_STATUS_CODES`. */
  status: number[];
  capacityCbm: (number | null)[];
  capacityQty: (number | null)[];
  usedCbm: number[];
  usedQty: number[];
  hasNoDim: number[];
  binCount: number[];
  binUsed: number[];
  layout: (Omit<StorageLayoutRow, 'storageID'> | null)[];
}

/** `GET /storages/map` outcome: `null` when the server answered 304. */
export interface WarehouseMapResult {
  payload: WarehouseMapPayload | null;
  etag: string | null;
}

/**
 * `GET /storages` in paged mode (page is sent): `{ data, pagination }`, rows in
 * tree order. Without `page` the endpoint answers the plain array instead.
 */
export interface ListStoragesPageQuery extends PageQuery {
  hubID: number;
  category?: StorageCategory | null;
  /** Type of the row's location (SECTION / BIN inherit it). */
  type?: StorageType | null;
  status?: StorageStatus | null;
  /** Name of the storage or of any ancestor (ILIKE). */
  search?: string | null;
  /** Only the DIRECT children of this storage (a Location's Sections, …). */
  parentID?: string | null;
  withStock?: boolean;
}

export interface CreateStoragePayload {
  hubID: number;
  hubName: string;
  hubCode: string | null;
  name: string;
  category: StorageCategory;
  type?: StorageType;
  parentID?: string;
}

/** PATCH /storages/:id — only sent fields change; category / parent never do. */
export interface UpdateStoragePayload {
  name?: string;
  /** LOCATION rows only. */
  type?: StorageType;
  /** INACTIVE cascades to everything under it; ACTIVE needs active parents. */
  status?: StorageStatus;
  /* capacity — null clears */
  widthCm?: number | null;
  depthCm?: number | null;
  heightCm?: number | null;
  capacityCbm?: number | null;
  capacityKg?: number | null;
  capacityQty?: number | null;
}

/**
 * SKU master properties (the columns of ops' master_sku sheet). Optional on
 * reads so rows from older builds still type-check; the server returns all.
 */
export interface SkuProperties {
  /** "Client / Brand". */
  brand?: string | null;
  productCategory?: string | null;
  productType?: string | null;
  productVariant?: string | null;
  color?: string | null;
  size?: string | null;
  material?: string | null;
  /** Storage condition of the goods ("Standard"…), not the storage tree type. */
  storageType?: string | null;
  batchTracking?: boolean;
  expiryTracking?: boolean;
  serialNumberTracking?: boolean;
  imeiTracking?: boolean;
  qcRequired?: boolean;
  fragile?: boolean;
  stackable?: boolean;
  pcsPerInnerBox?: number | null;
  innerBoxPerCarton?: number | null;
  pcsPerCarton?: number | null;
  cartonPerPallet?: number | null;
  pcsPerPallet?: number | null;
  /** "Barcode / EAN". */
  barcode?: string | null;
  /** Sample photo — a main-bucket URL from `POST /uploads/images`. */
  imageUrl?: string | null;
  status?: SkuStatus;
}

export interface WarehouseSku extends SkuProperties {
  id: string;
  clientID: number;
  client: ClientRef | null;
  sku: string;
  itemName: string;
  uomName: string;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  cbmPerUnit: number | null;
  /** v2 — per-unit weight used for surat jalan + shipment items. */
  weightKg?: number | null;
  createdAt: string;
  updatedAt?: string;
}

/**
 * GET /skus. `page` is always sent (paginated answer); `skus` resolves exact
 * codes (the lines of one form), `search` matches SKU code or item name.
 */
export interface ListSkusQuery extends PageQuery {
  clientID?: number | null;
  /** SKU code, name, brand or barcode (ILIKE). */
  search?: string | null;
  skus?: string[] | null;
  status?: SkuStatus | null;
  productCategory?: string | null;
}

/** Every SKU property as sent on create / import (blank optional = null). */
export interface SkuRowPayload extends SkuProperties {
  sku: string;
  itemName: string;
  uomName: string;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  weightKg?: number | null;
}

export interface CreateSkuPayload extends SkuRowPayload {
  clientID: number;
  clientName: string;
}

/** PATCH /skus/:id — SKU code and client are immutable. */
export type UpdateSkuPayload = Partial<Omit<SkuRowPayload, 'sku'>>;

export interface ImportSkusResult {
  created: number;
  skippedDuplicates: number;
}

/** The storage holding a stock row — any level of the tree. */
export interface StockStorage {
  id: string;
  name: string;
  category: StorageCategory;
  path: string;
  type?: StorageType | null;
}

/**
 * What kind of stock a row is: goods received on a RETURN purchase order are
 * RETURN, everything else REGULAR. A different type is always a different
 * stock row (same storage + SKU + batch included); picking takes both.
 */
export type WarehouseStockType = 'REGULAR' | 'RETURN';

export interface WarehouseStock {
  id: string;
  storageID: string;
  /** The holding storage's own name. */
  storageName: string;
  /** The holding storage itself: its category says which level the stock sits at. */
  storage: StockStorage;
  /** Lineage of the holding storage; a row held directly in a location is its own `location`. */
  location?: StorageLineage;
  /** Same, for a section (absent when the stock sits in a location). */
  section?: Omit<StorageLineage, 'type'>;
  hubID: number;
  clientID: number;
  client: ClientRef | null;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  stockType: WarehouseStockType;
  qtyOnHand: number;
  qtyReserved: number;
  qtyAvailable: number;
}

/** Stock filters shared by GET /stocks and GET /stocks/summary. */
export interface StockFilterQuery {
  clientID?: number | null;
  /** Any level — the storage's whole subtree. */
  storageID?: string | null;
  /** SKU, item, batch number, storage name (own or an ancestor's), client (ILIKE). */
  search?: string | null;
  /** expired_date range; batches without expiry drop out when set. */
  expiryFrom?: string | null;
  expiryTo?: string | null;
  /** Only REGULAR or only RETURN rows; omitted = both. */
  stockType?: WarehouseStockType | null;
}

/** GET /stocks — paginated; zero-qty batches are never returned. */
export interface ListStocksQuery extends PageQuery, StockFilterQuery {
  hubID: number;
  sku?: string | null;
}

export interface StockSummaryQuery extends StockFilterQuery {
  hubID: number | null;
  /** Only stock held in locations of this type — `QUARANTINE` feeds the
   *  "Stock quarantine" tile; omitted = every location. */
  locationType?: 'QUARANTINE' | null;
}

/** `groupBy` of GET /stocks/export; omitted = one row per batch. */
export type StockCsvGroupBy =
  'expired_date' | 'bin' | 'section' | 'location' | 'warehouse' | 'vendor_name';

/** GET /stocks/export — the list's filters, plus how to group the rows. */
export interface StockInventoryCsvQuery extends StockFilterQuery {
  hubID: number;
  groupBy?: StockCsvGroupBy | null;
}

/**
 * GET /stock-movements/export and /stock-mutations/export: a WIB day range of
 * at most 31 days (a 422 VALIDATION on `dateFrom` / `dateTo` otherwise).
 */
export interface StockPeriodCsvQuery {
  hubID: number;
  clientID?: number | null;
  /** SKU codes of that client; none = every SKU. */
  skus?: string[] | null;
  /** `YYYY-MM-DD`, inclusive. */
  dateFrom: string;
  dateTo: string;
}

/** A CSV the server built: the bytes and the name it gave the file. */
export interface CsvFile {
  blob: Blob;
  filename: string;
}

/** GET /stocks/availability — allocatable qty, the rule the backend reserves by. */
export interface StockAvailability {
  sku: string;
  available: number;
}

export interface StockMovement {
  id: string;
  hubID: number;
  clientID: number;
  clientName: string;
  sku: string;
  batchNumber: string;
  type: StockMovementType;
  refType: StockMovementRefType;
  refID: string;
  refCode: string;
  /** Name path of the storage the qty left ("Zona B / Rak 1 / Slot 1"); null on an inbound. */
  storageFromName: string | null;
  /** Name path of the storage the qty entered; null on an outbound. */
  storageToName: string | null;
  qtyDelta: number;
  createdBy: string;
  createdAt: string;
}

/** `POST /v1/warehouses/uploads/main` — a PDF stored in the main bucket. */
export interface UploadedDocument {
  fileUrl: string;
  fileName: string;
  sizeBytes: number;
}

/* transfer storage */

export type TransferStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'PREPARING'
  | 'DONE'
  | 'REJECTED'
  | 'CANCELLED';

/** One storage-to-storage transfer document (header + counts; no lines / events). */
export interface TransferSummary {
  id: string;
  code: string;
  hubID: number;
  hub: { id: number; name: string } | null;
  status: TransferStatus;
  sourceStorageID: string;
  sourceStorageName: string;
  /** "Location / Section / Bin" (names). */
  sourceStoragePath: string;
  destStorageID: string;
  destStorageName: string;
  destStoragePath: string;
  notes: string;
  lineCount: number;
  totalQty: number;
  approvedAt: string | null;
  approvedBy: string | null;
  preparingAt: string | null;
  preparingBy: string | null;
  doneAt: string | null;
  doneBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectReason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransferLine {
  lineNo: number | null;
  /** The source stock row reserved at create; null on rows from before approvals. */
  stockID: string | null;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  /** The moved row's type, kept at the destination. */
  stockType: WarehouseStockType;
  qty: number;
}

/** Timeline entry of a transfer; `status` is the status after the event. */
export interface TransferEvent {
  id: string;
  status: TransferStatus | null;
  title: string;
  note: string;
  actor: string;
  occurredAt: string;
}

/** GET /transfers/:id — lines in entry order, events newest first. */
export interface TransferDetail extends TransferSummary {
  lines: TransferLine[];
  events: TransferEvent[];
}

/** GET /transfers/summary — tab counts. */
export interface TransferStatusSummary {
  counts: Record<TransferStatus, number>;
  total: number;
}

/** Filters shared by the transfer list and its summary. */
export interface TransferFilterQuery extends CreatedRangeQuery {
  hubID?: number | null;
  search?: string | null;
  sourceStorageID?: string | null;
  destStorageID?: string | null;
  createdBy?: string | null;
}

export interface ListTransfersQuery extends PageQuery, TransferFilterQuery {
  status?: TransferStatus | null;
}

export interface CreateTransferPayload {
  hubID: number;
  hubName?: string;
  sourceStorageID: string;
  destStorageID: string;
  notes?: string;
  lines: {
    sku: string;
    batchNumber: string;
    /** Which row to move; needed only when the source holds the batch as both types. */
    stockType?: WarehouseStockType | null;
    qty: number;
  }[];
}

/* transfer warehouse (hub → hub) */

/**
 * Effective status: the request cycle (REQUESTED → FIRST_APPROVED → APPROVED /
 * REJECTED) is stored; from APPROVED on it is projected from the TRANSFER
 * outbound order and its TRANSFER purchase order.
 */
export type HubTransferStatus =
  | 'REQUESTED'
  | 'FIRST_APPROVED'
  | 'APPROVED'
  | 'PREPARING'
  | 'READY_TO_PICKUP'
  | 'SHIPPED'
  | 'PARTIAL_RECEIPT'
  | 'RECEIVED'
  | 'REJECTED'
  | 'CANCELLED';

export type HubTransferSide = 'SOURCE' | 'DESTINATION' | 'ANY';

/** One hub-to-hub transfer request (header + counts; no lines / events). */
export interface HubTransferSummary {
  id: string;
  code: string;
  status: HubTransferStatus;
  hubID: number;
  hub: HubRef | null;
  destinationHubID: number;
  destinationHub: HubRef | null;
  clientID: number;
  client: ClientRef | null;
  expectedDeliveryDate: string;
  notes: string;
  lineCount: number;
  totalQty: number;
  firstApprovedAt: string | null;
  firstApprovedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectReason: string | null;
  /** The TRANSFER outbound order raised at the destination's approval. */
  outboundOrder: { id: string; code: string; status: string } | null;
  /** The TRANSFER purchase order (inbound request) born at shipping. */
  transferPurchaseOrder: {
    id: string;
    code: string;
    receiptStatus: string | null;
  } | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface HubTransferLine {
  lineNo: number | null;
  sku: string;
  itemName: string;
  uomName: string;
  /** Allocatable qty at the source hub when the request was made. */
  qtyAvailable: number;
  qty: number;
}

/** Timeline entry of a hub transfer; `status` is the status after the event. */
export interface HubTransferEvent {
  id: string;
  status: HubTransferStatus | null;
  title: string;
  note: string;
  actor: string;
  occurredAt: string;
}

/** GET /hub-transfers/:id — lines in entry order, events newest first. */
export interface HubTransferDetail extends HubTransferSummary {
  recipientName: string;
  recipientPhone: string;
  destinationAddress: string;
  destinationLatitude: number | null;
  destinationLongitude: number | null;
  destinationNotes: string;
  lines: HubTransferLine[];
  events: HubTransferEvent[];
}

export interface CreateHubTransferPayload {
  hubID: number;
  hubName: string;
  hubCode: string | null;
  destinationHubID: number;
  destinationHubName: string;
  /** Left out when the destination hub has no code. */
  destinationHubCode?: string;
  clientID: number;
  clientName: string;
  clientCode?: string;
  /** `YYYY-MM-DD`. */
  expectedDeliveryDate: string;
  notes?: string;
  recipientName: string;
  recipientPhone: string;
  destinationAddress: string;
  destinationLatitude: number;
  destinationLongitude: number;
  destinationNotes?: string;
  lines: { sku: string; qty: number }[];
}

/** Filters shared by the hub transfer list and its tab counts. */
export interface HubTransferFilterQuery extends CreatedRangeQuery {
  /** The page's hub; null / omitted = all hubs. */
  hubID?: number | null;
  /** Which end of the transfer `hubID` is (default ANY = source or destination). */
  side?: HubTransferSide;
  clientID?: number | null;
  sourceHubID?: number | null;
  destinationHubID?: number | null;
  createdBy?: string | null;
  search?: string | null;
}

export interface ListHubTransfersQuery
  extends PageQuery, HubTransferFilterQuery {
  /** ONE status (the effective one); null = every status. */
  status?: HubTransferStatus | null;
}

/** GET /hub-transfers/summary — tab counts. */
export interface HubTransferStatusSummary {
  counts: Record<HubTransferStatus, number>;
  total: number;
}

/* stock opname */

export type StockOpnameStatus =
  | 'WAITING_MANAGER_APPROVAL'
  | 'WAITING_FAT_APPROVAL'
  | 'DONE'
  | 'REJECTED_BY_MANAGER'
  | 'REJECTED_BY_FAT'
  | 'CANCELLED';

/** Decided at create from the gaps: any gap != 0 needs the two approvals. */
export type StockOpnameType = 'WITH_ADJUSTMENT' | 'WITHOUT_ADJUSTMENT';

/** One opname document (header + totals; no lines / events). */
export interface StockOpnameSummary {
  id: string;
  code: string;
  hubID: number;
  hub: { id: number; name: string; code?: string | null } | null;
  clientID: number;
  client: ClientRef | null;
  type: StockOpnameType;
  status: StockOpnameStatus;
  notes: string;
  /** Berita acara / countsheet (uploaded document URL). */
  reportUrl: string;
  lineCount: number;
  /** Lines whose physical count differs from the system. */
  gapLineCount: number;
  totalQtySystem: number;
  totalQtyPhysical: number;
  /** Signed: physical − system, summed. */
  totalGap: number;
  approvedAt: string | null;
  approvedBy: string | null;
  fatApprovedAt: string | null;
  fatApprovedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectReason: string | null;
  doneAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface StockOpnameLine {
  lineNo: number | null;
  storageID: string;
  storageName: string;
  /** "Location / Section / Bin" (names). */
  storagePath: string;
  /** The counted lot; null for a new batch until FAT posts it. */
  stockID: string | null;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  /** The counted row's type; a new batch's is the type the found stock is booked as. */
  stockType: WarehouseStockType;
  /** Stock found physically that the system did not have. */
  isNewBatch: boolean;
  qtySystem: number;
  qtyPhysical: number;
  /** physical − system. */
  qtyGap: number;
  notes: string;
}

/** Timeline entry of an opname; `status` is the status after the event. */
export interface StockOpnameEvent {
  id: string;
  status: StockOpnameStatus | null;
  title: string;
  note: string;
  actor: string;
  occurredAt: string;
}

/** GET /stock-opnames/:id — lines in entry order, events newest first. */
export interface StockOpnameDetail extends StockOpnameSummary {
  lines: StockOpnameLine[];
  events: StockOpnameEvent[];
}

/** GET /stock-opnames/summary — tab counts. */
export interface StockOpnameStatusSummary {
  counts: Record<StockOpnameStatus, number>;
  total: number;
}

/** Filters shared by the opname list and its summary. */
export interface StockOpnameFilterQuery extends CreatedRangeQuery {
  hubID?: number | null;
  clientID?: number | null;
  type?: StockOpnameType | null;
  search?: string | null;
  createdBy?: string | null;
}

export interface ListStockOpnamesQuery
  extends PageQuery, StockOpnameFilterQuery {
  /** One status, or several comma-separated (the "Ditolak" tab). */
  status?: string | null;
}

export interface ListOpnameBatchesQuery extends PageQuery {
  hubID: number;
  clientID: number;
  storageID?: string | null;
  sku?: string | null;
  search?: string | null;
  excludeOpnameID?: string | null;
}

/** GET /stock-opnames/available-batches — a lot that can be counted. */
export interface OpnameBatch {
  stockID: string;
  storageID: string;
  storageName: string;
  storagePath: string;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  /** Absent when the server does not send it: then the line sends no type. */
  stockType?: WarehouseStockType;
  qtyOnHand: number;
  qtyReserved: number;
  /** Counted in another open opname. */
  locked: boolean;
  /** Code of that opname. */
  lockedBy?: string | null;
}

/** One line of the import dry-run: the CSV row resolved against the stock. */
export interface OpnameImportLine {
  lineIndex: number;
  storageID: string;
  storageName: string;
  sku: string;
  itemName: string;
  uomName: string;
  batchNumber: string;
  expiredDate: string | null;
  /** The counted row's type, or the new batch's (blank in the CSV = REGULAR). */
  stockType: WarehouseStockType;
  qtySystem: number;
  qtyPhysical: number;
  qtyGap: number;
  notes: string;
}

/** POST /stock-opnames/import — a dry run; nothing is saved. */
export interface OpnameImportResult {
  totalData: number;
  totalSuccess: number;
  totalFail: number;
  /** "Baris N: pesan" per failed row. */
  messages: string[];
  /** One entry per problem (row · CSV column · cell · why). Absent on older servers: use `messages`. */
  errors?: CsvImportError[];
  lines: OpnameImportLine[];
}

export interface CreateStockOpnamePayload {
  hubID: number;
  hubName?: string;
  clientID: number;
  clientName?: string;
  notes?: string;
  reportUrl: string;
  lines: {
    storageID: string;
    sku: string;
    /** Empty / omitted = a batch the system does not know yet. */
    batchNumber?: string;
    expiredDate?: string | null;
    /**
     * New batch: the type the found stock is booked as (default REGULAR).
     * Existing batch: which row to count; needed only when the storage holds
     * it as both types.
     */
    stockType?: WarehouseStockType | null;
    qtyPhysical: number;
    notes?: string;
  }[];
}
